import React from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * 卡片变体
   * - default: 默认卡片 (墨黑背景)
   * - highlight: 高亮卡片 (烫金边框)
   * - glass: 毛玻璃效果卡片
   */
  variant?: 'default' | 'highlight' | 'glass';
  /**
   * 是否显示悬停效果
   */
  hoverable?: boolean;
  /**
   * 是否添加内边距
   */
  padding?: boolean;
  /**
   * 边框样式
   */
  bordered?: boolean;
  /**
   * 阴影级别
   */
  shadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'gold';
}

const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  hoverable = false,
  padding = true,
  bordered = true,
  shadow = 'md',
  className = '',
  ...props
}) => {
  // 基础样式类
  const baseClasses = 'rounded-2xl transition-all duration-300';
  
  // 变体样式类
  const variantClasses = {
    default: 'card',
    highlight: 'card card-highlight',
    glass: 'card card-glass',
  };
  
  // 阴影样式类
  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
    gold: 'shadow-gold',
  };
  
  // 悬停效果
  const hoverClass = hoverable ? 'card-hover' : '';
  
  // 边框样式
  const borderClass = bordered ? 'border border-neutral-800' : '';
  
  // 内边距
  const paddingClass = padding ? 'p-6 md:p-8' : '';
  
  // 组合所有样式类
  const cardClasses = [
    baseClasses,
    variantClasses[variant],
    shadowClasses[shadow],
    hoverClass,
    borderClass,
    paddingClass,
    className
  ].filter(Boolean).join(' ');
  
  return (
    <div className={cardClasses} {...props}>
      {children}
    </div>
  );
};

// Card.Header 组件
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
}

const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  children,
  className = '',
  ...props
}) => {
  return (
    <div className={`mb-6 ${className}`} {...props}>
      {title && (
        <h3 className="text-2xl font-bold text-brand-accent font-serif-chinese mb-2">
          {title}
        </h3>
      )}
      {subtitle && (
        <p className="text-neutral-400 text-sm">{subtitle}</p>
      )}
      {children}
    </div>
  );
};

// Card.Body 组件
export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

const CardBody: React.FC<CardBodyProps> = ({
  children,
  noPadding = false,
  className = '',
  ...props
}) => {
  const paddingClass = noPadding ? '' : 'py-2';
  return (
    <div className={`${paddingClass} ${className}`} {...props}>
      {children}
    </div>
  );
};

// Card.Footer 组件
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'left' | 'center' | 'right';
}

const CardFooter: React.FC<CardFooterProps> = ({
  children,
  align = 'left',
  className = '',
  ...props
}) => {
  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[align];
  
  return (
    <div className={`mt-6 pt-4 border-t border-neutral-800 ${alignClass} ${className}`} {...props}>
      {children}
    </div>
  );
};

// 复合组件导出
const CardCompound = Card as typeof Card & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};

CardCompound.Header = CardHeader;
CardCompound.Body = CardBody;
CardCompound.Footer = CardFooter;

export default CardCompound;
